import React, { useMemo, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  READ_SECTION_MAX_ROWS,
  type ReadLensSectionCard,
  useReadLensSection,
} from '~/hooks/apis/reporting/useReadLensSection';

import type { LensCard, LensCardData, LensReadingCache, LensSection } from '~/types/leLens';

import { readingIsCurrent } from './viewSignature';

export type SectionReadingProps = {
  section: LensSection;
  /** The section's own cards, in spec order. */
  cards: ReadonlyArray<LensCard>;
  cardDataFor: (cardId: string) => LensCardData | undefined;
  /** The period the board is showing, as the header states it. */
  period: string;
  /** The fingerprint of everything this reading would depend on. */
  signature: string;
  /** Persisted onto the section so the next reader does not pay for it again. */
  onPersist?: (cache: LensReadingCache) => Promise<void> | void;
};

/**
 * The one written paragraph a section is allowed.
 *
 * A computed reading sits on every card and costs nothing, so this exists only
 * for what arithmetic cannot do: read several cards together. It is therefore
 * rationed, asked for rather than run on open, and never shown against numbers
 * it was not written from.
 */
export default function SectionReading({
  section,
  cards,
  cardDataFor,
  period,
  signature,
  onPersist,
}: SectionReadingProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const readSection = useReadLensSection();
  const [fresh, setFresh] = useState<LensReadingCache | null>(null);
  const [failed, setFailed] = useState(false);

  const reading = section.reading;
  const question = reading?.mode === 'narrative' ? reading.question : undefined;
  const cached = reading?.mode === 'narrative' ? reading.cached : undefined;

  // A reading written under this exact view is shown; one written under any
  // other view is withheld rather than shown against numbers it never saw.
  const current = fresh ?? (readingIsCurrent(cached, signature) ? cached : undefined);
  const presentedContent = current ? presentSectionReading(current.content) : '';
  const stale = Boolean(cached?.content) && !current;

  const payload = useMemo(
    () =>
      cards
        .map((card) => readableCard(card, cardDataFor(card.id)))
        .filter((card): card is ReadLensSectionCard => Boolean(card)),
    [cards, cardDataFor],
  );

  if (!question) {
    return null;
  }

  const run = async () => {
    setFailed(false);
    const content = await readSection.mutateAsync({ question, period, cards: payload });
    if (!content) {
      setFailed(true);
      return;
    }
    const next = { content, refreshedAt: new Date().toISOString(), viewSignature: signature };
    setFresh(next);
    await onPersist?.(next);
  };

  const working = readSection.isPending;
  const hasReadingState = Boolean(current || stale || failed);

  return (
    <Box
      sx={{
        backgroundColor: hasReadingState ? theme.palette.background.paper : 'transparent',
        border: hasReadingState ? `1px solid ${alpha(theme.palette.text.primary, 0.12)}` : 'none',
        borderLeftColor: current ? tokens.accent : alpha(theme.palette.text.primary, 0.24),
        borderLeftWidth: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 1.5,
        px: hasReadingState ? 2 : 0,
        py: hasReadingState ? 1.5 : 0,
      }}
    >
      {current && presentedContent ? (
        <>
          <Typography
            // The board PDF export prints this reading under the section heading, with its provenance.
            data-lens-pdf-reading={`Written by askRichard from ${payload.length === 1 ? 'this card' : `these ${payload.length} cards`}`}
            sx={{ m: 0, width: '100%' }}
            variant="body2"
          >
            {presentedContent}
          </Typography>
          <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {/* Coral marks model-written text and nothing else on this board, so
                provenance reads at a glance: a computed reading never carries it. */}
            <Box
              sx={{
                backgroundColor: tokens.coralAccent,
                borderRadius: '50%',
                boxShadow: `0 0 0 1px ${alpha(theme.palette.text.primary, 0.5)}`,
                flex: 'none',
                height: 7,
                width: 7,
              }}
            />
            <Typography sx={{ color: 'text.secondary' }} variant="caption">
              Written by askRichard from {payload.length === 1 ? 'this card' : `these ${payload.length} cards`}
            </Typography>
            <Button disabled={working} onClick={() => void run()} size="small" variant="text">
              {working ? 'Re-reading…' : 'Re-read'}
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {/* The section heading already prints the question, so repeating it
              here would be the same sentence twice. Only the two states the
              heading cannot express get words. */}
          {(current || stale || failed) && (
            <Typography sx={{ color: 'text.secondary', flex: '1 1 280px', m: 0 }} variant="body2">
              {current && !presentedContent
                ? 'Add a comparison period or another movement card, then read this section again.'
                : stale
                  ? 'The saved reading was written for a different view, so it is not shown.'
                  : failed
                    ? 'This section could not be read. The cards below are unaffected.'
                    : null}
            </Typography>
          )}
          <Button
            disabled={working || !payload.length}
            onClick={() => void run()}
            size="small"
            startIcon={working ? <CircularProgress size={14} /> : <AutoAwesomeOutlinedIcon fontSize="small" />}
            variant="outlined"
          >
            {working ? 'Reading…' : stale || failed ? 'Read again' : 'Read this section'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * Model-written readings occasionally lead with a limitation and then give a
 * useful answer anyway. The dashboard should lead with the answer. Strip only
 * a leading failure sentence; never rewrite or manufacture the evidence that
 * follows it. If the response is limitation only, return nothing so the UI
 * offers a concrete recovery action instead of presenting a dead end as an
 * insight.
 */
export function presentSectionReading(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) return '';
  // A sentence boundary needs punctuation followed by the next capitalised
  // sentence. A bare full stop is not enough: currency and percentage values
  // routinely contain decimals that must stay untouched.
  const boundary = trimmed.search(/[.!?]\s+(?=[A-Z])/);
  const first = boundary === -1 ? trimmed : trimmed.slice(0, boundary + 1);
  const limitation = /\b(?:cannot|can't|could not|unable to|does not|do not)\b/i;
  const missingEvidence = /\b(?:attribut|answer|support|determin|compar|evidence|data)\w*\b/i;
  if (!limitation.test(first) || !missingEvidence.test(first)) return trimmed;
  return boundary === -1 ? '' : trimmed.slice(boundary + 1).trim();
}

/**
 * One card as the reading should see it: what the chart is drawing, not what
 * the query returned.
 *
 * A card with no rows, or one whose rows are row-level detail rather than an
 * aggregate, contributes nothing and is left out. Sending raw detail would be
 * both useless to the reading and the one way row-level data could reach a
 * model from this path.
 */
function readableCard(card: LensCard, data: LensCardData | undefined): ReadLensSectionCard | undefined {
  const rows = data?.rows;
  const columns = data?.columns;
  if (!rows?.length || !columns?.length) return undefined;
  if (data?.status !== 'ready' && data?.status !== 'empty') return undefined;
  if (!data.aggregated && rows.length > READ_SECTION_MAX_ROWS) return undefined;

  const note =
    data.truncated || rows.length > READ_SECTION_MAX_ROWS
      ? `Showing ${Math.min(rows.length, READ_SECTION_MAX_ROWS)} of ${data.totalRowCount ?? rows.length} rows`
      : undefined;

  return {
    title: card.title,
    type: card.type,
    columns,
    rows: rows.slice(0, READ_SECTION_MAX_ROWS),
    ...(note ? { truncatedNote: note } : {}),
  };
}
